import { BusyWatcher, Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty } from "utils/filters";
import ListViewModel from "../../listViewModel";
import type OutgoingPayGatePaymentResponse from "entities/outgoingPayGatePaymentResponse";
import OutgoingPayGateRepository from "repositories/outgoingPayGateRepository";
import CardGatewayDetailViewModel from "./cardGatewayDetailViewModel";
import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";
import download from "downloadjs";
import OutgoingPayGatePaymentsRequest, {
  OutgoingPayGatePaymentsRequestValidationRules,
} from "entities/outgoingPayGatePaymentsRequest";
import { format } from "date-fns-tz";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import OutgoingPayGatePaymentStatus from "entities/outgoingPayGatePaymentStatus";
import OptionsService from "services/optionsService";
import type LicensePlate from "entities/licensePlate";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type PagingManagerViewModel from "../../pagingManagerViewModel";

const navigationName = "card-gateway";

@Router.registerRoute({ name: Router.Self, route: navigationName, children: [CardGatewayDetailViewModel] })
export default class CardGatewayViewModel extends ListViewModel<
  OutgoingPayGatePaymentResponse,
  CardGatewayDetailViewModel,
  ScreenBase,
  OutgoingPayGatePaymentsRequest
> {
  readonly modelName = "outgoingPayGatePayment";
  @observable downloadBusyWatcher = new BusyWatcher();
  countrySelectViewModel: CountrySelectViewModel;
  protected abortController?: AbortController;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: OutgoingPayGateRepository,
    protected detailViewModelFactory: ReturnType<typeof CardGatewayDetailViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    private pagingManagerViewModelFactory: ReturnType<typeof PagingManagerViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.countryId,
          this.filter.variableSymbol,
          this.filter.paidAmount,
          this.filter.authorizedDate?.from,
          this.filter.authorizedDate?.to,
          this.filter.exportedDate?.from,
          this.filter.exportedDate?.to,
          this.filter.cardNumber,
          this.filter.arn,
          this.filter.status,
          this.filter.officialRecordId,
          this.filter.licensePlate,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    super.onInitialize();
  }

  protected detailParams(itemId: string) {
    const pagingManager = this.pagingManagerViewModelFactory(this, itemId);
    return [pagingManager];
  }

  @computed
  get name() {
    return this.translate("paygate");
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get allowReportDownload() {
    return this.securityService.allowTo("finance.reports");
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  @bound
  download() {
    const downloadAction = async () => {
      const response = await this.repository.getAllOutgoingPayGatePaymentsExport(this.filter);
      download((response as any).data, `report-${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
    };
    this.downloadBusyWatcher.watch<void>(downloadAction());
  }

  @computed
  get statusOptions() {
    return this.optionsService.internalEnumOptions(OutgoingPayGatePaymentStatus);
  }

  protected createFilter() {
    return attachAutomaticValidator(new OutgoingPayGatePaymentsRequest(), OutgoingPayGatePaymentsRequestValidationRules);
  }

  protected async fetchData() {
    if (this.isFilterEmpty) {
      return emptyListResponse();
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    const { limit, offset } = this.pagingFilter;
    this.abortController = new AbortController();

    const data = await this.repository.getAllOutgoingPayGatePayments(
      { limit, offset },
      this.appliedFilter,
      this.abortController.signal
    );
    this.abortController = undefined;

    return data;
  }

  protected resetFilterValues(filter: OutgoingPayGatePaymentsRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `outgoingPayments.${code}`, size);
  }

  translateOutgoingPayGatePaymentStatus(status: OutgoingPayGatePaymentStatus) {
    return this.localizationService.translateEnum("outgoingPayGatePaymentStatus", status);
  }

  translateModelAttribute(model: "licensePlate", attribute: keyof LicensePlate): string;
  translateModelAttribute(model: "licensePlate", attribute: keyof LicensePlate) {
    return this.localizationService.translateAttribute(model, attribute);
  }
}
